import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { get, filter } from 'lodash';
import ButterToast, { POS_TOP, POS_CENTER } from 'butter-toast/dist/lean';
import { IPAD_MIN_WIDTH_THRESHOLD, PC_MIN_WIDTH_THRESHOLD, collaborationEventBus, RenderIf } from '../Utils';
import { sendMixPanelEvent } from '../Utils/mixpanel';
import { CLOSED_FILE_VIEWER, SELECT_COMMENT, VIEWED_COLLABORATION } from '../Utils/mixpanel/mixpanelEvents';
import { MARKER_CLICK_EVENT, PROGRESS_CLICK_EVENT, MEDIA_PLAY_EVENT } from '../MediaViewer/consts';
import CollaborationCommentShape from './PropsShape/CollaborationCommentShape';
import getSteps from './CollaborationWalkthroughSteps';
import WalkthroughWrapper from './WalkthroughWrapper';
import MediaContainer from './components/MediaContainer';
import CollaborationSidebar from './components/CollaborationSidebar';
import Loader from './components/Loader';
import MediaOverlay from './components/MediaOverlay';
import MediaController from './components/MediaController';
import NameModal from './components/NameModal';
import RemovedCallaboratorErrorPage from './components/RemovedCallaboratorErrorPage';
import * as Selectors from './selectors';
import * as FilerrsSelectors from './selectors/filerrs';
import * as SettingsSelectors from './selectors/settings';
import * as CurrentsSelectors from './selectors/current';
import * as GroupsSelectors from './selectors/groups';
import * as CommentsSelectors from './selectors/comments';
import * as CollaboratorsSelectors from './selectors/collaborators';
import * as ErrorsSelectors from './selectors/errors';
import * as LoadingsSelectors from './selectors/loading';
import * as Translations from './providers/i18n';

import './style.scss';

const COLLABORATION_WRAPPER_CLASSNAME = 'collaboration-wrapper';
export const COLLABORATION_BASE_CLASSNAME = `${COLLABORATION_WRAPPER_CLASSNAME} attachments_preview-package order_page_perseus collaboration_perseus`;

export class StatelessRoot extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loadedMedia: null,
            walkThroughSteps: [],
            seenWalkThrough: false
        };
        this.updateRootRef = this.updateRootRef.bind(this);
        this.mediaLoaded = this.mediaLoaded.bind(this);
        this.beforeUnloadHandler = this.beforeUnloadHandler.bind(this);
        this.setWalkThroughSteps = this.setWalkThroughSteps.bind(this);
        this.shouldStartWalkthroughTour = this.shouldStartWalkthroughTour.bind(this);
        this.onMarkerClick = this.onMarkerClick.bind(this);
        this.onProgressClick = this.onProgressClick.bind(this);
        this.onPlay = this.onPlay.bind(this);
        this.handleSeenWalkThrough = this.handleSeenWalkThrough.bind(this);
    }

    componentDidMount() {
        window.addEventListener('beforeunload', this.beforeUnloadHandler);

        // Toggler is opened by default for PCs
        // and closed by default for IPADS and MOBILE
        const shouldDisplayOpen = (window.innerWidth >= PC_MIN_WIDTH_THRESHOLD);

        this.trackPageViewEvent();

        // eslint-disable-next-line react/no-did-mount-set-state
        this.setState({
            shouldDisplayOpen,
            togglerOpen: shouldDisplayOpen
        });
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.shouldStartWalkthroughTour(prevProps, prevState)) {
            this.setWalkThroughSteps();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    }

    trackPageViewEvent() {
        const {
            currentCollaboratorInvitePermissions,
            activeParticipatorsInCurrentGroup,
            isCollaborationEnabled
        } = this.props;

        if (!isCollaborationEnabled) {
            // without this, the event is sent twice !
            return;
        }

        sendMixPanelEvent({
            eventName: VIEWED_COLLABORATION,
            eventData: {
                'Has share permissions': currentCollaboratorInvitePermissions,
                'Number of active participants': activeParticipatorsInCurrentGroup.length
            }
        });
    }

    shouldStartWalkthroughTour(prevProps, prevState) {
        // If collaboration is enabled and we're in IPADs/PCs screens
        // and walkthrough steps wasn't configured
        if (this.props.isLoading || !this.props.isCollaborationEnabled || window.innerWidth < IPAD_MIN_WIDTH_THRESHOLD) {
            return false;
        }

        // If we got currentCollaboratorId (which means that we got collaboration data) and toggler is opened
        if (
            !prevProps.currentCollaboratorId &&
            this.props.currentCollaboratorId &&
            this.state.togglerOpen
        ) {
            return true;
        }

        if (
            prevProps.isLoading &&
            !this.props.isLoading &&
            this.state.togglerOpen
        ) {
            return true;
        }

        // If we add the first pending comment
        if (
            !prevProps.pendingCommentsIds.length &&
            this.props.pendingCommentsIds.length &&
            this.state.togglerOpen
        ) {
            return true;
        }

        // If we've opened the toggler and we have currentCollaboratorId (which means that we've collaboration data)
        if (
            !prevState.togglerOpen &&
            this.state.togglerOpen &&
            this.props.currentCollaboratorId
        ) {
            return true;
        }

        return false;
    }

    beforeUnloadHandler(event) {
        const activeElement = get(event, 'target.activeElement');
        if (activeElement && activeElement.hasAttribute('data-download-link')) {
            document.querySelector(`.${COLLABORATION_WRAPPER_CLASSNAME}`).focus();
            return;
        }

        const { pendingCommentsIds } = this.props;

        if (pendingCommentsIds.length) {
            event.preventDefault();
            event.returnValue = '';
        }

        sendMixPanelEvent({
            eventName: CLOSED_FILE_VIEWER,
            eventData: {
                'Close Source': 'Browser',
                'Has Pending Comments': !!pendingCommentsIds.length
            }
        });
    }

    updateRootRef(rootRef) {
        if (!this.state.rootRef) {
            this.setState({
                rootRef
            });
        }
    }

    mediaLoaded(event) {
        this.setState({ loadedMedia: event });
    }

    setWalkThroughSteps() {
        const { currentFilerrPreviewType, comments, currentCollaboratorId, currentCollaboratorPermissions, groupsCount, i18n } = this.props;
        const othersComments = filter(comments, (comment) => comment.collaboratorId !== currentCollaboratorId);

        this.setState({
            walkThroughSteps: getSteps(currentFilerrPreviewType, othersComments.length, currentCollaboratorPermissions, groupsCount, i18n)
        });
    }

    onMarkerClick(marker) {
        collaborationEventBus.emit(MARKER_CLICK_EVENT, marker);

        sendMixPanelEvent({
            eventName: SELECT_COMMENT,
            eventData: {
                'Opened Comment Source': 'Timeline Markers'
            }
        });
    }

    handleSeenWalkThrough(seen) {
        this.setState({
            seenWalkThrough: seen
        });
    }

    onProgressClick(data) {
        collaborationEventBus.emit(PROGRESS_CLICK_EVENT, data);
    }

    onPlay() {
        collaborationEventBus.emit(MEDIA_PLAY_EVENT);
    }

    render() {
        if (this.props.isRemovedCollaboratorError) {
            return <RemovedCallaboratorErrorPage/>;
        }

        const {
            close,
            isCollaborationEnabled,
            currentCollaboratorUsername,
            isUnsubscribing
        } = this.props;

        const {
            loadedMedia,
            shouldDisplayOpen,
            seenWalkThrough
        } = this.state;

        const shouldRenderCollaboration = shouldDisplayOpen !== undefined && isCollaborationEnabled;
        const shouldShowNameModal = isUnsubscribing || !currentCollaboratorUsername && seenWalkThrough;

        return (
            <div className={COLLABORATION_BASE_CLASSNAME} tabIndex={0} ref={this.updateRootRef}>
                <div className="previewer-content">
                    {shouldRenderCollaboration && <CollaborationSidebar shouldDisplayOpen={shouldDisplayOpen}
                        onOpen={() => this.setState({ togglerOpen: true })}
                        onClose={() => this.setState({ togglerOpen: false })}/>}
                    <MediaContainer onMediaLoad={this.mediaLoaded}
                        loadedMedia={loadedMedia}
                        onMarkerClick={this.onMarkerClick}
                        onProgressClick={this.onProgressClick}
                        onPlay={this.onPlay}
                        close={close}>
                        <RenderIf condition={isCollaborationEnabled}>
                            <MediaOverlay media={loadedMedia}/>
                            <MediaController media={loadedMedia}/>
                        </RenderIf>
                    </MediaContainer>
                    <WalkthroughWrapper steps={this.state.walkThroughSteps} handleSeenWalkThrough={this.handleSeenWalkThrough}/>
                    {shouldShowNameModal && <NameModal/>}
                    <ButterToast namespace="collaboration-wrapper" position={{ vertical: POS_TOP, horizontal: POS_CENTER }}/>
                </div>
            </div>
        );
    }
}

const withLoadingState = (Component) => {
    function Wrapper(props) {
        const { isLoading } = props;

        if (isLoading) {
            return <Loader/>;
        }

        return <Component {...props}/>;
    }

    Wrapper.propTypes = {
        isLoading: PropTypes.bool
    };

    Wrapper.displayName = `WithLoadingState(${Component.displayName || Component.name || 'Component'})`;

    return Wrapper;
};

StatelessRoot.propTypes = {
    close: PropTypes.func,
    currentFilerrPreviewType: PropTypes.string,
    isCollaborationEnabled: PropTypes.bool,
    pendingCommentsIds: PropTypes.array,
    comments: PropTypes.arrayOf(CollaborationCommentShape), // eslint-disable-line react/no-unused-prop-types
    currentCollaboratorId: PropTypes.string,
    currentCollaboratorPermissions: PropTypes.object,
    groupsCount: PropTypes.number,
    isLoading: PropTypes.bool,
    currentCollaboratorUsername: PropTypes.string,
    currentCollaboratorInvitePermissions: PropTypes.bool,
    activeParticipatorsInCurrentGroup: PropTypes.array,
    isRemovedCollaboratorError: PropTypes.bool,
    isUnsubscribing: PropTypes.bool,
    i18n: PropTypes.shape({
        t: PropTypes.func
    })
};

const mapStateToProps = (state) => ({
    currentFilerrPreviewType: FilerrsSelectors.getCurrentFilerrPreviewType(state),
    isCollaborationEnabled: SettingsSelectors.isCollaborationAsked(state) && Selectors.isCollaborationDataReady(state),
    pendingCommentsIds: CommentsSelectors.getPendingIds(state),
    groupsCount: GroupsSelectors.getGroupsLength(state),
    comments: CommentsSelectors.getCommentsByCurrentFileAndCurrentGroup(state),
    currentCollaboratorId: CurrentsSelectors.getCurrentCollaboratorId(state),
    currentCollaboratorPermissions: CollaboratorsSelectors.getCurrentCollaboratorPermissions(state),
    currentCollaboratorUsername: CollaboratorsSelectors.getCurrentCollaboratorUsername(state),
    isRemovedCollaboratorError: ErrorsSelectors.isRemovedCollaboratorError(state),
    isLoading: LoadingsSelectors.isDataviewLoading(state),
    isUnsubscribing: SettingsSelectors.getWithUnsubscribe(state),
    currentCollaboratorInvitePermissions: CollaboratorsSelectors.getCurrentCollaboratorInvitePermissions(state),
    activeParticipatorsInCurrentGroup: CollaboratorsSelectors.getActiveParticipatorsByCurrentGroup(state)
});

export default compose(
    Translations.connect,
    connect(mapStateToProps))(
    withLoadingState(StatelessRoot)
);
